import React, { useMemo, useRef, useState } from "react";
import { Image as PdfImage, StyleSheet, Text, View } from "@react-pdf/renderer";
import DataList from "components/datatable/DataList";
import useAxios from "hooks/useAxios";
import useAxiosFunction from "hooks/useAxiosFunction";
import usePDFRenderer from "hooks/usePDFRenderer";
import { useAuth } from "context/AuthProvider";
import { MyConst } from "data/MyConst";
import { getStrDateLong, postgresDateToIndonesia, RuleRequired } from "utils/MyUtils";
import { PersonalEducationTable } from "features/kms/profile/PersonalEducationHistoryPage";
import {
  PersonalAchievementTable,
  PersonalCertificationTable,
} from "features/kms/profile/PersonalCapaianPages";
import { boolText, hasPermission, showEmpty } from "features/kms/system/SystemPageUtils";
import { formatPersonName } from "features/kms/common/nameFormat";

function toOptions(items, valueKey, labelKey = "nama") {
  if (!Array.isArray(items)) return [];
  return items.map((item) => ({
    ...item,
    __value: item?.[valueKey] ? String(item[valueKey]) : "",
    __label:
      typeof labelKey === "function" ? labelKey(item) : item?.[labelKey] || "",
  }));
}

function pekerjaanAddConfig(title = "Tambah Pekerjaan") {
  return {
    title,
    url: "/references/pekerjaan",
    cellid: "id_pekerjaan",
    columns: ["nama"],
    coltype: {
      nama: {
        type: "text",
        label: "Nama Pekerjaan",
        name: "nama",
        validation: RuleRequired,
        colsize: 12,
      },
    },
    optionsUrl: "/references/pekerjaan",
    optionsAuth: true,
    otherLabel: "Lainnya",
  };
}

function detailValue(value) {
  return showEmpty(value);
}

function detailDate(value) {
  return value ? postgresDateToIndonesia(value) || getStrDateLong(value) : "";
}

function membershipPeriod(item) {
  const start = detailDate(item?.tanggal_mulai);
  const end = item?.tanggal_selesai ? detailDate(item.tanggal_selesai) : "Sekarang";
  return `${start || "-"} s.d. ${end || "Sekarang"}`;
}

function formatWholeNumber(value) {
  if (value === null || value === undefined || value === "") return "";
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return String(value);
  return new Intl.NumberFormat("id-ID", {
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  }).format(numeric);
}

function formatAge(value) {
  if (value === null || value === undefined || value === "") return "";
  return `${formatWholeNumber(value)} Tahun`;
}

function formatBirthCertificate(item) {
  const number = String(item?.nomor_akta_lahir || "").trim();
  const year = String(item?.tahun_akta_lahir || "").trim();
  const parts = [];
  if (number) parts.push(`Nomor ${number}`);
  if (year) parts.push(`Tahun ${year}`);
  return parts.join(" ");
}

function membershipHistoryItems(item) {
  return Array.isArray(item?.membership_history) ? item.membership_history : [];
}

function educationHistoryItems(item) {
  return Array.isArray(item?.education_history) ? item.education_history : [];
}

function organizationHistoryItems(item) {
  return Array.isArray(item?.pengurus_history) ? item.pengurus_history : [];
}

function certificationHistoryItems(item) {
  return Array.isArray(item?.certification_history) ? item.certification_history : [];
}

function achievementHistoryItems(item) {
  return Array.isArray(item?.achievement_history) ? item.achievement_history : [];
}

function sdmProfilePrintId(id) {
  return `sdm-profile-detail-${String(id || "").replace(/[^a-zA-Z0-9_-]/g, "")}`;
}

function photoSource(filename) {
  return filename
    ? `${String(MyConst.URLASET || "").replace(/\/$/, "")}/personal/${filename}`
    : "";
}

function personalAssetSource(filename) {
  return filename
    ? `${String(MyConst.URLASET || "").replace(/\/$/, "")}/personal-document/${encodeURIComponent(filename)}`
    : "";
}

function photoAPIAssetSource(filename) {
  return filename
    ? `${String(MyConst.URLAPI || "").replace(/\/$/, "")}/file/asset/personal/${encodeURIComponent(filename)}`
    : "";
}

export async function loadPhotoDataURL(filename, token) {
  const url = photoAPIAssetSource(filename);
  if (!url) return "";

  try {
    const response = await fetch(url, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!response.ok) return "";

    const blob = await response.blob();
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result || "");
      reader.onerror = () => resolve("");
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    return "";
  }
}

function formatFullName(item) {
  return formatPersonName(item, ["nama_lengkap"]);
}

function formatBankAccount(item) {
  const bank = String(item?.bank || "").trim();
  const accountNumber = String(item?.nomor_rekening || "").trim();
  const accountName = String(item?.rekening_atas_nama || "").trim();
  const firstPart = [bank, accountNumber].filter(Boolean).join(" ");
  const parts = [];
  if (firstPart) parts.push(firstPart);
  if (accountName) parts.push(`a.n. ${accountName}`);
  return parts.join(" ");
}

function DetailField({ label, value, size = 4, className = "" }) {
  return (
    <div className={`col-md-${size} mb-3 ${className}`}>
      <div className="text-xs text-uppercase text-muted font-weight-bold">{label}</div>
      <div className="border-bottom pb-2">{detailValue(value)}</div>
    </div>
  );
}

function DetailSection({ title, children }) {
  return (
    <div className="card mb-3">
      <div className="card-body pb-1">
        {title && <h5 className="sdm-profile-tab-title d-none">{title}</h5>}
        <div className="row">{children}</div>
      </div>
    </div>
  );
}

function supportingDocumentItems(item) {
  return [
    { label: "Kartu Keluarga", file: item?.file_kartu_keluarga },
    { label: "KTP", file: item?.file_ktp },
    { label: "Akta Lahir", file: item?.file_akta_lahir },
    { label: "Rekening Bank", file: item?.file_rekening_bank },
  ].filter((doc) => String(doc.file || "").trim() !== "");
}

function SupportingDocumentsSection({ item }) {
  const documents = supportingDocumentItems(item);
  if (documents.length < 1) return null;

  return (
    <div className="card mb-3">
      <div className="card-header bg-light">
        <h3 className="card-title font-nol-koma-sembilan">Dokumen Pendukung</h3>
      </div>
      <div className="card-body">
        <div className="row">
          {documents.map((doc) => (
            <div className="col-md-3 mb-3" key={doc.label}>
              <div className="border rounded p-3 h-100 bg-light">
                <div className="text-xs text-uppercase text-muted font-weight-bold mb-2">
                  {doc.label}
                </div>
                <a
                  href={personalAssetSource(doc.file)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-outline-primary btn-sm"
                >
                  <i className="fa fa-file-alt mr-1"></i>
                  Lihat Dokumen
                </a>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function SDMProfileDetail({
  item,
  title,
  printId,
  photoAction = null,
  extraTabs = [],
  educationUrl = "",
  achievementUrl = "",
  certificationUrl = "",
  achievementOptionsBaseUrl = "",
}) {
  const [activeTab, setActiveTab] = useState("identitas");
  const photoUrl = item?.photo_url
    ? `${String(MyConst.URLASET || "").replace(/\/$/, "")}/personal/${item.photo_url}`
    : `${process.env.PUBLIC_URL || ""}/assets/dist/img/avatar.png`;
  const fullName = formatFullName(item);
  const birthPlaceDate = [item?.tempat_lahir, detailDate(item?.tanggal_lahir)]
    .filter(Boolean)
    .join(", ");
  const bankAccount = formatBankAccount(item);
  const indukOrganisasiCabor = item?.induk_organisasi_cabor_name || "";
  const klubOlahraga = item?.klub_olahraga_name || "";
  const membershipHistory = membershipHistoryItems(item);
  const educationHistory = educationHistoryItems(item);
  const organizationHistory = organizationHistoryItems(item);
  const achievementHistory = achievementHistoryItems(item);
  const certificationHistory = certificationHistoryItems(item);

  const tabs = [
    { id: "identitas", label: "Identitas" },
    { id: "pekerjaan", label: "Pekerjaan" },
    { id: "orang_tua", label: "Orang Tua" },
    { id: "asuransi", label: "Asuransi" },
    { id: "pendidikan", label: "Pendidikan" },
    { id: "prestasi", label: "Prestasi" },
    { id: "sertifikasi", label: "Sertifikasi" },
    { id: "riwayat", label: "Keanggotaan" },
    { id: "organisasi", label: "Riwayat Organisasi" },
    ...extraTabs.map((tab) => ({ id: tab.id, label: tab.label })),
  ];

  return (
    <div id={printId} className="sdm-profile-detail">
      <div className="card bg-light mb-3">
        <div className="card-body">
          <div className="d-flex flex-column flex-md-row align-items-center">
            <div className="text-center">
              <img
                src={photoUrl}
                alt={fullName || "Profil SDM"}
                className="img-circle elevation-2 bg-white"
                style={{
                  width: 112,
                  height: 112,
                  objectFit: "cover",
                  objectPosition: "center",
                  border: "3px solid #fff",
                }}
              />
              {photoAction ? <div className="mt-2">{photoAction}</div> : null}
            </div>
            <div className="ml-md-4 mt-3 mt-md-0 text-center text-md-left flex-fill">
              <div className="text-uppercase text-sm font-weight-bold text-muted">
                PROFIL {String(title || "").toUpperCase()}
              </div>
              <h3 className="mb-1">{detailValue(fullName)}</h3>
              <div className="text-muted mb-2">
                <span className="mr-4">
                  <strong>Induk Organisasi Cabor</strong>: {detailValue(indukOrganisasiCabor)}
                </span>
                {klubOlahraga && (
                  <span className="mr-4">
                    <strong>Klub Olahraga</strong>: {detailValue(klubOlahraga)}
                  </span>
                )}
                <span className="mr-4">
                  <strong>Cabor</strong>: {detailValue(item?.cabor_name)}
                </span>
              </div>
              <div>
                <span className="badge badge-secondary mr-2">No. Induk: {detailValue(item?.nomor_induk)}</span>
                <span className={`badge ${item?.aktif ? "badge-success" : "badge-secondary"}`}>
                  {boolText(item?.aktif)}
                </span>
                <span className="ml-2 text-muted">
                  <strong>Sejak</strong>: {detailValue(detailDate(item?.tanggal_mulai))}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="card card-primary card-outline card-outline-tabs">
        <div className="card-header p-0 border-bottom-0">
          <ul className="nav nav-tabs" role="tablist">
            {tabs.map((tab) => (
              <li className="nav-item" key={tab.id}>
                <button
                  type="button"
                  className={`nav-link px-4 py-2 ${activeTab === tab.id ? "active" : ""}`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.label}
                </button>
              </li>
            ))}
          </ul>
        </div>
        <div className="card-body">
          <div className={`sdm-profile-tab-pane ${activeTab === "identitas" ? "" : "d-none"}`}>
            <DetailSection title="Identitas">
              <DetailField label="NIK" value={item?.nik} size={3} />
              <DetailField label="No. KK" value={item?.no_kk} size={3} />
              <DetailField label="Nama Lengkap" value={fullName} size={6} />
              <DetailField label="Tempat, Tanggal Lahir" value={birthPlaceDate} size={3} />
              <DetailField label="Usia" value={formatAge(item?.usia)} size={3} />
              <DetailField label="No. Akta Lahir" value={item?.nomor_akta_lahir} size={3} />
              <DetailField label="Tahun Akta Lahir" value={item?.tahun_akta_lahir} size={3} />
              <DetailField label="Jenis Kelamin" value={item?.jenis_kelamin_name} size={3} />
              <DetailField label="Golongan Darah" value={item?.golongan_darah} size={3} />
              <DetailField label="Agama" value={item?.agama_name} size={3} />
              <DetailField label="Kewarganegaraan" value={item?.kewarganegaraan} size={3} />
              <DetailField label="Status Perkawinan" value={item?.status_perkawinan_name} size={3} />
              <DetailField label="HP" value={item?.hp} size={3} />
              <DetailField label="Telepon" value={item?.telepon} size={3} />
              <DetailField label="Email" value={item?.email} size={3} />
              <DetailField label="Alamat" value={item?.alamat} size={6} />
              <DetailField label="Kab/Kota" value={item?.kota} size={3} />
              <DetailField label="Kode POS" value={item?.kode_pos} size={3} />
              <DetailField label="Rekening Bank" value={bankAccount} size={12} />
            </DetailSection>
            <SupportingDocumentsSection item={item} />
          </div>
          <div className={`sdm-profile-tab-pane ${activeTab === "pekerjaan" ? "" : "d-none"}`}>
            <DetailSection title="Pekerjaan">
              <DetailField label="Pekerjaan" value={item?.pekerjaan} size={3} />
              <DetailField label="Jabatan" value={item?.jabatan} size={3} />
              <DetailField label="Bidang Usaha" value={item?.bidang_usaha} size={3} />
              <DetailField label="Nama Tempat Kerja" value={item?.nama_tempat_kerja} size={3} />
              <DetailField label="Telepon Tempat Kerja" value={item?.telepon_tempat_kerja} size={3} />
              <DetailField label="Alamat Tempat Kerja" value={item?.alamat_tempat_kerja} size={6} />
              <DetailField label="Pendapatan" value={formatWholeNumber(item?.pendapatan)} size={3} />
              <DetailField label="Sumber Pendapatan" value={item?.sumber_pendapatan} size={3} />
              <DetailField label="NPWP" value={item?.npwp} size={3} />
            </DetailSection>
          </div>
          <div className={`sdm-profile-tab-pane ${activeTab === "orang_tua" ? "" : "d-none"}`}>
            <DetailSection title="Orang Tua">
              <DetailField label="Nama Ayah" value={item?.nama_ayah} size={3} />
              <DetailField label="Pekerjaan Ayah" value={item?.pekerjaan_ayah} size={3} />
              <DetailField label="Nama Ibu" value={item?.nama_ibu} size={3} />
              <DetailField label="Pekerjaan Ibu" value={item?.pekerjaan_ibu} size={3} />
              <DetailField label="Alamat Orang Tua" value={item?.alamat_orang_tua} size={12} />
            </DetailSection>
          </div>
          <div className={`sdm-profile-tab-pane ${activeTab === "asuransi" ? "" : "d-none"}`}>
            <DetailSection title="Asuransi">
              <DetailField label="No. BPJS Kesehatan" value={item?.nomor_bpjs_kesehatan} size={3} />
              <DetailField label="Status BPJS Kesehatan" value={item?.status_bpjs_kesehatan} size={3} />
              <DetailField label="No. BPJS Ketenagakerjaan" value={item?.nomor_bpjs_ketenagakerjaan} size={3} />
              <DetailField label="Status BPJS Ketenagakerjaan" value={item?.status_bpjs_ketenagakerjaan} size={3} />
              <DetailField label="Asuransi Lainnya" value={item?.asuransi_lainnya} size={6} />
              <DetailField label="Nomor Asuransi Lainnya" value={item?.nomor_asuransi_lainnya} size={6} />
            </DetailSection>
          </div>
          <div className={`sdm-profile-tab-pane ${activeTab === "pendidikan" ? "" : "d-none"}`}>
            <DetailSection title="Pendidikan">
              <div className="col-12">
                {educationUrl ? (
                  <PersonalEducationTable baseUrl={educationUrl} />
                ) : educationHistory.length < 1 ? (
                  <div className="text-muted">Riwayat pendidikan belum tersedia.</div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-sm table-bordered mb-2">
                      <thead>
                        <tr>
                          <th style={{ width: 50 }} className="text-center">No</th>
                          <th>Jenjang Pendidikan</th>
                          <th>Program Studi</th>
                          <th>Nama Institusi</th>
                          <th>Alamat</th>
                          <th style={{ width: 90 }} className="text-center">Tahun Lulus</th>
                          <th style={{ width: 120 }} className="text-center">Bulan Lulus</th>
                        </tr>
                      </thead>
                      <tbody>
                        {educationHistory.map((education, index) => (
                          <tr key={education?.id_riwayat_pendidikan || index}>
                            <td className="text-center">{index + 1}</td>
                            <td>{detailValue(education?.jenjang_pendidikan)}</td>
                            <td>{detailValue(education?.program_studi)}</td>
                            <td>{detailValue(education?.nama_institusi)}</td>
                            <td>{detailValue(education?.alamat)}</td>
                            <td className="text-center">{detailValue(education?.tahun_lulus)}</td>
                            <td className="text-center">{detailValue(education?.bulan_lulus_name)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </DetailSection>
          </div>
          <div className={`sdm-profile-tab-pane ${activeTab === "prestasi" ? "" : "d-none"}`}>
            <DetailSection title="Prestasi">
              <div className="col-12">
                {achievementUrl ? (
                  <PersonalAchievementTable
                    baseUrl={achievementUrl}
                    optionsBaseUrl={achievementOptionsBaseUrl}
                    peranOptionsUrl={`${achievementOptionsBaseUrl}/peran-options`}
                  />
                ) : achievementHistory.length < 1 ? (
                  <div className="text-muted">Prestasi belum tersedia.</div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-sm table-bordered mb-2">
                      <thead>
                        <tr>
                          <th style={{ width: 50 }} className="text-center">No</th>
                          <th>Peran</th>
                          <th>Nama Pertandingan</th>
                          <th>Cabor</th>
                          <th>Nomor Pertandingan</th>
                          <th>Tingkat</th>
                          <th>Hasil</th>
                          <th style={{ width: 70 }} className="text-center">Skor</th>
                          <th style={{ width: 120 }} className="text-center">Tanggal</th>
                          <th style={{ width: 120 }} className="text-center">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {achievementHistory.map((achievement, index) => (
                          <tr key={achievement?.id_personal_prestasi || index}>
                            <td className="text-center">{index + 1}</td>
                            <td>{detailValue(achievement?.peran_name)}</td>
                            <td>{detailValue(achievement?.nama_pertandingan)}</td>
                            <td>{detailValue(achievement?.cabor_name)}</td>
                            <td>{detailValue(achievement?.nomor_pertandingan_name)}</td>
                            <td>{detailValue(achievement?.tingkat_name)}</td>
                            <td>{detailValue(achievement?.hasil_name)}</td>
                            <td className="text-center">{detailValue(achievement?.skor)}</td>
                            <td className="text-center">{detailValue(detailDate(achievement?.tanggal_awal))}</td>
                            <td className="text-center">{detailValue(achievement?.status_name)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </DetailSection>
          </div>
          <div className={`sdm-profile-tab-pane ${activeTab === "sertifikasi" ? "" : "d-none"}`}>
            <DetailSection title="Sertifikasi">
              <div className="col-12">
                {certificationUrl ? (
                  <PersonalCertificationTable
                    baseUrl={certificationUrl}
                    peranOptionsUrl={`${achievementOptionsBaseUrl}/peran-options`}
                  />
                ) : certificationHistory.length < 1 ? (
                  <div className="text-muted">Sertifikasi belum tersedia.</div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-sm table-bordered mb-2">
                      <thead>
                        <tr>
                          <th style={{ width: 50 }} className="text-center">No</th>
                          <th>Peran</th>
                          <th>Nama Sertifikasi</th>
                          <th>Jenis</th>
                          <th>Tingkat</th>
                          <th>Lembaga Penyelenggara</th>
                          <th>Nomor</th>
                          <th style={{ width: 120 }} className="text-center">Tanggal</th>
                          <th style={{ width: 120 }} className="text-center">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {certificationHistory.map((certification, index) => (
                          <tr key={certification?.id_personal_sertifikasi || index}>
                            <td className="text-center">{index + 1}</td>
                            <td>{detailValue(certification?.peran_name)}</td>
                            <td>{detailValue(certification?.nama_sertifikasi)}</td>
                            <td>{detailValue(certification?.jenis_sertifikasi_name)}</td>
                            <td>{detailValue(certification?.tingkat_name)}</td>
                            <td>{detailValue(certification?.lembaga_penyelenggara)}</td>
                            <td>{detailValue(certification?.nomor)}</td>
                            <td className="text-center">{detailValue(detailDate(certification?.tanggal_sertifikat))}</td>
                            <td className="text-center">{detailValue(certification?.status_name)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </DetailSection>
          </div>
          <div className={`sdm-profile-tab-pane ${activeTab === "riwayat" ? "" : "d-none"}`}>
            <DetailSection title="Keanggotaan">
              <div className="col-12">
                {membershipHistory.length < 1 ? (
                  <div className="text-muted">Riwayat keanggotaan belum tersedia.</div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-sm table-bordered mb-2">
                      <thead>
                        <tr>
                          <th style={{ width: 50 }} className="text-center">No</th>
                          <th>Peran</th>
                          <th>No. Induk</th>
                          <th>Cabor</th>
                          <th>Induk Organisasi Cabor</th>
                          <th>Klub Olahraga</th>
                          <th className="text-center">Periode</th>
                          <th className="text-center">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {membershipHistory.map((history, index) => (
                          <tr key={history?.id_keanggotaan || index}>
                            <td className="text-center">{index + 1}</td>
                            <td>{detailValue(history?.peran_name)}</td>
                            <td>{detailValue(history?.nomor_induk)}</td>
                            <td>{detailValue(history?.cabor_name)}</td>
                            <td>{detailValue(history?.induk_organisasi_cabor_name)}</td>
                            <td>{detailValue(history?.klub_olahraga_name)}</td>
                            <td className="text-center">{detailValue(membershipPeriod(history))}</td>
                            <td className="text-center">{boolText(history?.aktif)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </DetailSection>
          </div>
          <div className={`sdm-profile-tab-pane ${activeTab === "organisasi" ? "" : "d-none"}`}>
            <DetailSection title="Riwayat Organisasi">
              <div className="col-12">
                {organizationHistory.length < 1 ? (
                  <div className="text-muted">Riwayat organisasi belum tersedia.</div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-sm table-bordered mb-2">
                      <thead>
                        <tr>
                          <th style={{ width: 50 }} className="text-center">No</th>
                          <th>Organisasi</th>
                          <th>Jabatan</th>
                          <th className="text-center">Periode</th>
                          <th className="text-center">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {organizationHistory.map((history, index) => (
                          <tr key={history?.id_pengurus_organisasi || index}>
                            <td className="text-center">{index + 1}</td>
                            <td>{detailValue(history?.organisasi_name)}</td>
                            <td>{detailValue(history?.jabatan)}</td>
                            <td className="text-center">{detailValue(membershipPeriod(history))}</td>
                            <td className="text-center">{boolText(history?.aktif)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </DetailSection>
          </div>
          {extraTabs.map((tab) => (
            <div key={tab.id} className={`sdm-profile-tab-pane ${activeTab === tab.id ? "" : "d-none"}`}>
              {typeof tab.render === "function" ? tab.render() : tab.content}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const pdfProfileStyles = StyleSheet.create({
  title: {
    fontSize: 12,
    fontFamily: "Helvetica-Bold",
    textAlign: "center",
    marginBottom: 12,
  },
  sectionTitle: {
    fontSize: 10,
    fontFamily: "Helvetica-Bold",
    marginBottom: 6,
    marginTop: 2,
  },
  section: {
    marginBottom: 6,
    padding: 4,
  },
  lastSection: {
    marginBottom: 0,
  },
  contentRow: {
    flexDirection: "row",
    width: 515,
  },
  rowsWrap: {
    width: 385,
    paddingRight: 10,
  },
  row: {
    flexDirection: "row",
    width: 385,
    marginBottom: 2.5,
  },
  fullRow: {
    flexDirection: "row",
    width: 515,
    marginBottom: 2.5,
  },
  label: {
    width: 138,
    fontSize: 8.5,
    fontFamily: "Helvetica-Bold",
    lineHeight: 1.25,
  },
  colon: {
    width: 8,
    fontSize: 8.5,
    textAlign: "center",
    lineHeight: 1.25,
  },
  value: {
    width: 239,
    fontSize: 8.5,
    lineHeight: 1.25,
  },
  fullValue: {
    width: 349,
    fontSize: 8.5,
    lineHeight: 1.25,
  },
  photoWrap: {
    width: 130,
    alignItems: "flex-end",
    paddingTop: 0,
    padding: 4,
  },
  photo: {
    width: 82,
    height: 104,
    objectFit: "cover",
    borderWidth: 1,
    borderColor: "#333",
  },
  photoPlaceholder: {
    width: 82,
    height: 104,
    borderWidth: 1,
    borderColor: "#333",
    alignItems: "center",
    justifyContent: "center",
  },
  muted: {
    color: "#666",
    fontSize: 8,
  },
  historyHeaderRow: {
    flexDirection: "row",
    backgroundColor: "#f1f3f5",
    borderTopWidth: 1,
    borderLeftWidth: 1,
    borderColor: "#999",
  },
  historyRow: {
    flexDirection: "row",
    borderLeftWidth: 1,
    borderColor: "#999",
  },
  historyCell: {
    fontSize: 7,
    padding: 3,
    borderRightWidth: 1,
    borderBottomWidth: 1,
    borderColor: "#999",
  },
  historyHeaderCell: {
    fontSize: 7,
    fontFamily: "Helvetica-Bold",
    padding: 3,
    borderRightWidth: 1,
    borderBottomWidth: 1,
    borderColor: "#999",
  },
});

function pdfValue(value) {
  return detailValue(value);
}

function PdfProfileRow({ label, value, full = false }) {
  return (
    <View style={full ? pdfProfileStyles.fullRow : pdfProfileStyles.row}>
      <Text style={pdfProfileStyles.label}>{label}</Text>
      <Text style={pdfProfileStyles.colon}>:</Text>
      <Text style={full ? pdfProfileStyles.fullValue : pdfProfileStyles.value}>{pdfValue(value)}</Text>
    </View>
  );
}

function PdfProfileSection({ title, children, last = false }) {
  return (
    <View style={[pdfProfileStyles.section, last ? pdfProfileStyles.lastSection : null]}>
      <Text style={pdfProfileStyles.sectionTitle}>{title}</Text>
      {children}
    </View>
  );
}

function PdfHistoryCell({ children, width, header = false, center = false }) {
  return (
    <Text
      style={[
        header ? pdfProfileStyles.historyHeaderCell : pdfProfileStyles.historyCell,
        { width, textAlign: center ? "center" : "left" },
      ]}
    >
      {children}
    </Text>
  );
}

function PdfEducationHistory({ items = [] }) {
  return (
    <PdfProfileSection title="Pendidikan">
      {items.length < 1 ? (
        <Text style={pdfProfileStyles.muted}>Riwayat pendidikan belum tersedia.</Text>
      ) : (
        <View>
          <View style={pdfProfileStyles.historyHeaderRow}>
            <PdfHistoryCell width={24} header center>No</PdfHistoryCell>
            <PdfHistoryCell width={86} header>Jenjang Pendidikan</PdfHistoryCell>
            <PdfHistoryCell width={76} header>Program Studi</PdfHistoryCell>
            <PdfHistoryCell width={112} header>Nama Institusi</PdfHistoryCell>
            <PdfHistoryCell width={116} header>Alamat</PdfHistoryCell>
            <PdfHistoryCell width={50} header center>Tahun</PdfHistoryCell>
            <PdfHistoryCell width={51} header center>Bulan</PdfHistoryCell>
          </View>
          {items.map((education, index) => (
            <View key={education?.id_riwayat_pendidikan || index} style={pdfProfileStyles.historyRow} wrap={false}>
              <PdfHistoryCell width={24} center>{index + 1}</PdfHistoryCell>
              <PdfHistoryCell width={86}>{pdfValue(education?.jenjang_pendidikan)}</PdfHistoryCell>
              <PdfHistoryCell width={76}>{pdfValue(education?.program_studi)}</PdfHistoryCell>
              <PdfHistoryCell width={112}>{pdfValue(education?.nama_institusi)}</PdfHistoryCell>
              <PdfHistoryCell width={116}>{pdfValue(education?.alamat)}</PdfHistoryCell>
              <PdfHistoryCell width={50} center>{pdfValue(education?.tahun_lulus)}</PdfHistoryCell>
              <PdfHistoryCell width={51} center>{pdfValue(education?.bulan_lulus_name)}</PdfHistoryCell>
            </View>
          ))}
        </View>
      )}
    </PdfProfileSection>
  );
}

function PdfAchievementHistory({ items = [] }) {
  return (
    <PdfProfileSection title="Prestasi">
      {items.length < 1 ? (
        <Text style={pdfProfileStyles.muted}>Prestasi belum tersedia.</Text>
      ) : (
        <View>
          <View style={pdfProfileStyles.historyHeaderRow}>
            <PdfHistoryCell width={24} header center>No</PdfHistoryCell>
            <PdfHistoryCell width={56} header>Peran</PdfHistoryCell>
            <PdfHistoryCell width={108} header>Nama Pertandingan</PdfHistoryCell>
            <PdfHistoryCell width={70} header>Cabor</PdfHistoryCell>
            <PdfHistoryCell width={88} header>Nomor Pertandingan</PdfHistoryCell>
            <PdfHistoryCell width={63} header>Tingkat</PdfHistoryCell>
            <PdfHistoryCell width={58} header>Hasil</PdfHistoryCell>
            <PdfHistoryCell width={22} header center>Skor</PdfHistoryCell>
            <PdfHistoryCell width={26} header center>Status</PdfHistoryCell>
          </View>
          {items.map((achievement, index) => (
            <View key={achievement?.id_personal_prestasi || index} style={pdfProfileStyles.historyRow} wrap={false}>
              <PdfHistoryCell width={24} center>{index + 1}</PdfHistoryCell>
              <PdfHistoryCell width={56}>{pdfValue(achievement?.peran_name)}</PdfHistoryCell>
              <PdfHistoryCell width={108}>{pdfValue(achievement?.nama_pertandingan)}</PdfHistoryCell>
              <PdfHistoryCell width={70}>{pdfValue(achievement?.cabor_name)}</PdfHistoryCell>
              <PdfHistoryCell width={88}>{pdfValue(achievement?.nomor_pertandingan_name)}</PdfHistoryCell>
              <PdfHistoryCell width={63}>{pdfValue(achievement?.tingkat_name)}</PdfHistoryCell>
              <PdfHistoryCell width={58}>{pdfValue(achievement?.hasil_name)}</PdfHistoryCell>
              <PdfHistoryCell width={22} center>{pdfValue(achievement?.skor)}</PdfHistoryCell>
              <PdfHistoryCell width={26} center>{pdfValue(achievement?.status_name)}</PdfHistoryCell>
            </View>
          ))}
        </View>
      )}
    </PdfProfileSection>
  );
}

function PdfCertificationHistory({ items = [] }) {
  return (
    <PdfProfileSection title="Sertifikasi">
      {items.length < 1 ? (
        <Text style={pdfProfileStyles.muted}>Sertifikasi belum tersedia.</Text>
      ) : (
        <View>
          <View style={pdfProfileStyles.historyHeaderRow}>
            <PdfHistoryCell width={24} header center>No</PdfHistoryCell>
            <PdfHistoryCell width={62} header>Peran</PdfHistoryCell>
            <PdfHistoryCell width={118} header>Nama Sertifikasi</PdfHistoryCell>
            <PdfHistoryCell width={72} header>Jenis</PdfHistoryCell>
            <PdfHistoryCell width={72} header>Tingkat</PdfHistoryCell>
            <PdfHistoryCell width={95} header>Lembaga</PdfHistoryCell>
            <PdfHistoryCell width={46} header>Nomor</PdfHistoryCell>
            <PdfHistoryCell width={26} header center>Status</PdfHistoryCell>
          </View>
          {items.map((certification, index) => (
            <View key={certification?.id_personal_sertifikasi || index} style={pdfProfileStyles.historyRow} wrap={false}>
              <PdfHistoryCell width={24} center>{index + 1}</PdfHistoryCell>
              <PdfHistoryCell width={62}>{pdfValue(certification?.peran_name)}</PdfHistoryCell>
              <PdfHistoryCell width={118}>{pdfValue(certification?.nama_sertifikasi)}</PdfHistoryCell>
              <PdfHistoryCell width={72}>{pdfValue(certification?.jenis_sertifikasi_name)}</PdfHistoryCell>
              <PdfHistoryCell width={72}>{pdfValue(certification?.tingkat_name)}</PdfHistoryCell>
              <PdfHistoryCell width={95}>{pdfValue(certification?.lembaga_penyelenggara)}</PdfHistoryCell>
              <PdfHistoryCell width={46}>{pdfValue(certification?.nomor)}</PdfHistoryCell>
              <PdfHistoryCell width={26} center>{pdfValue(certification?.status_name)}</PdfHistoryCell>
            </View>
          ))}
        </View>
      )}
    </PdfProfileSection>
  );
}

function PdfMembershipHistory({ items = [], last = false }) {
  return (
    <PdfProfileSection title="Keanggotaan" last={last}>
      {items.length < 1 ? (
        <Text style={pdfProfileStyles.muted}>Riwayat keanggotaan belum tersedia.</Text>
      ) : (
        <View>
          <View style={pdfProfileStyles.historyHeaderRow}>
            <PdfHistoryCell width={24} header center>No</PdfHistoryCell>
            <PdfHistoryCell width={62} header>Peran</PdfHistoryCell>
            <PdfHistoryCell width={72} header>No. Induk</PdfHistoryCell>
            <PdfHistoryCell width={75} header>Cabor</PdfHistoryCell>
            <PdfHistoryCell width={105} header>Induk Organisasi Cabor</PdfHistoryCell>
            <PdfHistoryCell width={86} header>Klub Olahraga</PdfHistoryCell>
            <PdfHistoryCell width={62} header center>Periode</PdfHistoryCell>
            <PdfHistoryCell width={29} header center>Status</PdfHistoryCell>
          </View>
          {items.map((history, index) => (
            <View key={history?.id_keanggotaan || index} style={pdfProfileStyles.historyRow} wrap={false}>
              <PdfHistoryCell width={24} center>{index + 1}</PdfHistoryCell>
              <PdfHistoryCell width={62}>{pdfValue(history?.peran_name)}</PdfHistoryCell>
              <PdfHistoryCell width={72}>{pdfValue(history?.nomor_induk)}</PdfHistoryCell>
              <PdfHistoryCell width={75}>{pdfValue(history?.cabor_name)}</PdfHistoryCell>
              <PdfHistoryCell width={105}>{pdfValue(history?.induk_organisasi_cabor_name)}</PdfHistoryCell>
              <PdfHistoryCell width={86}>{pdfValue(history?.klub_olahraga_name)}</PdfHistoryCell>
              <PdfHistoryCell width={62} center>{pdfValue(membershipPeriod(history))}</PdfHistoryCell>
              <PdfHistoryCell width={29} center>{boolText(history?.aktif)}</PdfHistoryCell>
            </View>
          ))}
        </View>
      )}
    </PdfProfileSection>
  );
}

function PdfOrganizationHistory({ items = [], last = false }) {
  return (
    <PdfProfileSection title="Riwayat Organisasi" last={last}>
      {items.length < 1 ? (
        <Text style={pdfProfileStyles.muted}>Riwayat organisasi belum tersedia.</Text>
      ) : (
        <View>
          <View style={pdfProfileStyles.historyHeaderRow}>
            <PdfHistoryCell width={28} header center>No</PdfHistoryCell>
            <PdfHistoryCell width={205} header>Organisasi</PdfHistoryCell>
            <PdfHistoryCell width={125} header>Jabatan</PdfHistoryCell>
            <PdfHistoryCell width={100} header center>Periode</PdfHistoryCell>
            <PdfHistoryCell width={57} header center>Status</PdfHistoryCell>
          </View>
          {items.map((history, index) => (
            <View key={history?.id_pengurus_organisasi || index} style={pdfProfileStyles.historyRow} wrap={false}>
              <PdfHistoryCell width={28} center>{index + 1}</PdfHistoryCell>
              <PdfHistoryCell width={205}>{pdfValue(history?.organisasi_name)}</PdfHistoryCell>
              <PdfHistoryCell width={125}>{pdfValue(history?.jabatan)}</PdfHistoryCell>
              <PdfHistoryCell width={100} center>{pdfValue(membershipPeriod(history))}</PdfHistoryCell>
              <PdfHistoryCell width={57} center>{boolText(history?.aktif)}</PdfHistoryCell>
            </View>
          ))}
        </View>
      )}
    </PdfProfileSection>
  );
}

const emptyFormStyles = StyleSheet.create({
  sectionTitle: {
    fontSize: 10,
    fontFamily: "Helvetica-Bold",
    marginTop: 8,
    marginBottom: 5,
    paddingBottom: 2,
    borderBottomWidth: 1,
    borderBottomColor: "#333",
  },
  row: {
    flexDirection: "row",
    minHeight: 21,
    marginBottom: 2.5,
  },
  label: {
    width: 150,
    fontSize: 9,
    paddingTop: 3,
  },
  colon: {
    width: 10,
    fontSize: 9,
    paddingTop: 3,
  },
  line: {
    flex: 1,
    borderBottomWidth: 0.7,
    borderBottomColor: "#555",
  },
  statement: {
    marginTop: 10,
    paddingTop: 6,
    borderTopWidth: 1,
    borderTopColor: "#333",
  },
  checkboxRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    marginTop: 6,
  },
  checkbox: {
    width: 10,
    height: 10,
    borderWidth: 1,
    borderColor: "#333",
    marginRight: 6,
    marginTop: 1,
  },
  statementText: {
    flex: 1,
    fontSize: 9,
    lineHeight: 1.35,
  },
  signatureWrap: {
    flexDirection: "row",
    justifyContent: "flex-end",
    marginTop: 18,
  },
  signatureBox: {
    width: 190,
  },
  signatureDate: {
    fontSize: 9,
    marginBottom: 36,
  },
  signatureNameLine: {
    borderBottomWidth: 0.7,
    borderBottomColor: "#555",
    height: 14,
    marginBottom: 3,
  },
  signatureLabel: {
    fontSize: 8,
    color: "#555",
    textAlign: "center",
  },
});

function EmptyFormRow({ label }) {
  return (
    <View style={emptyFormStyles.row}>
      <Text style={emptyFormStyles.label}>{label}</Text>
      <Text style={emptyFormStyles.colon}>:</Text>
      <View style={emptyFormStyles.line} />
    </View>
  );
}

function EmptyFormSection({ title, fields }) {
  return (
    <View wrap={false}>
      <Text style={emptyFormStyles.sectionTitle}>{title}</Text>
      {fields.map((field) => <EmptyFormRow key={field} label={field} />)}
    </View>
  );
}

function EmptyFormStatement({ title }) {
  return (
    <View style={emptyFormStyles.statement} wrap={false}>
      <View style={emptyFormStyles.checkboxRow}>
        <View style={emptyFormStyles.checkbox} />
        <Text style={emptyFormStyles.statementText}>
          Saya menyatakan bahwa seluruh data yang saya isi pada formulir ini adalah benar,
          lengkap, dan dapat dipertanggungjawabkan untuk keperluan pendataan {title || "SDM Keolahragaan"}.
        </Text>
      </View>
      <View style={emptyFormStyles.signatureWrap}>
        <View style={emptyFormStyles.signatureBox}>
          <Text style={emptyFormStyles.signatureDate}>................................, ...... / ...... / ............</Text>
          <View style={emptyFormStyles.signatureNameLine} />
          <Text style={emptyFormStyles.signatureLabel}>Nama Jelas dan Tanda Tangan</Text>
        </View>
      </View>
    </View>
  );
}

function SDMEmptyFormPDF({ title }) {
  return (
    <View>
      <Text style={pdfProfileStyles.title}>FORM DATA {String(title || "SDM").toUpperCase()}</Text>
      <EmptyFormSection title="Data Keanggotaan" fields={["Cabor", "Klub Olahraga", "No. KK", "NIK"]} />
      <EmptyFormSection title="Identitas" fields={["Nama Lengkap", "Tempat, Tanggal Lahir", "Nomor, Tahun Akta Lahir", "Jenis Kelamin", "Golongan Darah", "Agama", "Kewarganegaraan", "Status Perkawinan", "HP/Telepon", "Email", "Alamat", "Kab/Kota", "Kode POS", "Rekening Bank"]} />
      <EmptyFormSection title="Pekerjaan" fields={["Pekerjaan", "Jabatan di Tempat Kerja", "Bidang Usaha Tempat Kerja", "Nama Tempat Kerja", "Telepon Tempat Kerja", "Alamat Tempat Kerja", "Pendapatan", "Sumber Pendapatan", "NPWP"]} />
      <EmptyFormSection title="Orang Tua" fields={["Nama Ayah", "Pekerjaan Ayah", "Nama Ibu", "Pekerjaan Ibu", "Alamat Orang Tua"]} />
      <EmptyFormSection title="BPJS dan Asuransi" fields={["No. BPJS Kesehatan", "Status BPJS Kesehatan", "No. BPJS Ketenagakerjaan", "Status BPJS Ketenagakerjaan", "Asuransi Lainnya", "Nomor Asuransi Lainnya"]} />
      <EmptyFormStatement title={title} />
    </View>
  );
}

export function SDMProfilePDF({ item, title }) {
  const fullName = formatFullName(item);
  const birthPlaceDate = [item?.tempat_lahir, detailDate(item?.tanggal_lahir)]
    .filter(Boolean)
    .join(", ");
  const phoneLabel = [item?.hp, item?.telepon].filter(Boolean).join(" / ");
  const bankAccount = formatBankAccount(item);
  const photoUrl = item?.photo_data_url || "";
  const pendapatan = item?.pendapatan ? `Rp ${formatWholeNumber(item.pendapatan)}` : "";
  const aktaLahir = formatBirthCertificate(item);
  const indukOrganisasiCabor = item?.induk_organisasi_cabor_name || "";
  const klubOlahraga = item?.klub_olahraga_name || "";
  const membershipHistory = membershipHistoryItems(item);
  const educationHistory = educationHistoryItems(item);
  const organizationHistory = organizationHistoryItems(item);
  const achievementHistory = achievementHistoryItems(item);
  const certificationHistory = certificationHistoryItems(item);

  return (
    <View>
      <Text style={pdfProfileStyles.title}>PROFIL {String(title || "").toUpperCase()}</Text>
      <PdfProfileSection title="Data Pribadi">
        <View style={pdfProfileStyles.contentRow}>
          <View style={pdfProfileStyles.rowsWrap}>
            <PdfProfileRow label="Induk Organisasi Cabor" value={indukOrganisasiCabor} />
            {klubOlahraga ? <PdfProfileRow label="Klub Olahraga" value={klubOlahraga} /> : null}
            <PdfProfileRow label="Cabor" value={item?.cabor_name} />
            <PdfProfileRow label="NIK" value={item?.nik} />
            <PdfProfileRow label="Nomor Induk" value={item?.nomor_induk} />
            <PdfProfileRow label="No. KK" value={item?.no_kk} />
            <PdfProfileRow label="Nama Lengkap" value={fullName} />
            <PdfProfileRow label="Tempat, Tanggal Lahir" value={birthPlaceDate} />
            <PdfProfileRow label="Akta Lahir" value={aktaLahir} />
            <PdfProfileRow label="Jenis Kelamin" value={item?.jenis_kelamin_name} />
          </View>
          <View style={pdfProfileStyles.photoWrap}>
            {photoUrl ? (
              <PdfImage
                src={photoUrl}
                style={pdfProfileStyles.photo}
                cache={false}
              />
            ) : (
              <View style={pdfProfileStyles.photoPlaceholder}>
                <Text style={pdfProfileStyles.muted}>{photoUrl ? "Foto tersedia" : "Foto"}</Text>
              </View>
            )}
          </View>
        </View>
        <PdfProfileRow label="Golongan Darah" value={item?.golongan_darah} full />
        <PdfProfileRow label="Agama" value={item?.agama_name} full />
        <PdfProfileRow label="Kewarganegaraan" value={item?.kewarganegaraan} full />
        <PdfProfileRow label="Status Perkawinan" value={item?.status_perkawinan_name} full />
        <PdfProfileRow label="HP/Telepon" value={phoneLabel} full />
        <PdfProfileRow label="Email" value={item?.email} full />
        <PdfProfileRow label="Alamat" value={item?.alamat} full />
        <PdfProfileRow label="Kab/Kota" value={item?.kota} full />
        <PdfProfileRow label="Kode POS" value={item?.kode_pos} full />
        <PdfProfileRow label="Rekening Bank" value={bankAccount} full />
      </PdfProfileSection>
      <PdfProfileSection title="Data Pekerjaan">
        <PdfProfileRow label="Pekerjaan" value={item?.pekerjaan} full />
        <PdfProfileRow label="Jabatan di Tempat Kerja" value={item?.jabatan} full />
        <PdfProfileRow label="Bidang Usaha Tempat Kerja" value={item?.bidang_usaha} full />
        <PdfProfileRow label="Nama Tempat Kerja" value={item?.nama_tempat_kerja} full />
        <PdfProfileRow label="Telepon Tempat Kerja" value={item?.telepon_tempat_kerja} full />
        <PdfProfileRow label="Alamat Tempat Kerja" value={item?.alamat_tempat_kerja} full />
        <PdfProfileRow label="Pendapatan" value={pendapatan} full />
        <PdfProfileRow label="Sumber Pendapatan" value={item?.sumber_pendapatan} full />
        <PdfProfileRow label="NPWP" value={item?.npwp} full />
      </PdfProfileSection>
      <PdfProfileSection title="Data Orang Tua">
        <PdfProfileRow label="Nama Ayah" value={item?.nama_ayah} full />
        <PdfProfileRow label="Pekerjaan Ayah" value={item?.pekerjaan_ayah} full />
        <PdfProfileRow label="Nama Ibu" value={item?.nama_ibu} full />
        <PdfProfileRow label="Pekerjaan Ibu" value={item?.pekerjaan_ibu} full />
        <PdfProfileRow label="Alamat Orang Tua" value={item?.alamat_orang_tua} full />
      </PdfProfileSection>
      <PdfProfileSection title="Asuransi">
        <PdfProfileRow label="No. BPJS Kesehatan" value={item?.nomor_bpjs_kesehatan} full />
        <PdfProfileRow label="Status BPJS Kesehatan" value={item?.status_bpjs_kesehatan} full />
        <PdfProfileRow label="No. BPJS Ketenagakerjaan" value={item?.nomor_bpjs_ketenagakerjaan} full />
        <PdfProfileRow label="Status BPJS Ketenagakerjaan" value={item?.status_bpjs_ketenagakerjaan} full />
        <PdfProfileRow label="Asuransi Lainnya" value={item?.asuransi_lainnya} full />
        <PdfProfileRow label="Nomor Asuransi Lainnya" value={item?.nomor_asuransi_lainnya} full />
      </PdfProfileSection>
      <PdfEducationHistory items={educationHistory} />
      <PdfAchievementHistory items={achievementHistory} />
      <PdfCertificationHistory items={certificationHistory} />
      <PdfMembershipHistory items={membershipHistory} />
      <PdfOrganizationHistory items={organizationHistory} last />
    </View>
  );
}

export default function SDMKeolahragaanPage({ title, rolePath }) {
  const { user } = useAuth();
  const [, , , axiosFetch] = useAxiosFunction();
  const [, , loadingProfilePDF, axiosFetchProfilePDF] = useAxiosFunction();
  const [getProfilePDFViewer, toggleProfilePDFViewer] = usePDFRenderer();
  const [getEmptyFormPDFViewer, toggleEmptyFormPDFViewer] = usePDFRenderer();
  const [profilePDFData, setProfilePDFData] = useState(null);
  const lookupKeyRef = useRef("");
  const lookupTimerRef = useRef(null);
  const [addLookup, setAddLookup] = useState({
    status: "idle",
    exists: false,
    registered: false,
    message: "Isi Cabor dan NIK 16 digit untuk memulai pengecekan data.",
  });
  const [selectedCaborInduk, setSelectedCaborInduk] = useState({
    id_cabor: "",
    id_induk_organisasi: "",
  });
  const [selectedCaborID, setSelectedCaborID] = useState("");
  const [currentEditKlubOption, setCurrentEditKlubOption] = useState(null);
  const [caborResp] = useAxios({ method: "GET", url: "/references/cabor", auth: true });
  const [caborIndukResp] = useAxios({
    method: "GET",
    url: "/references/sdm-cabor-induk",
    auth: true,
  });
  const [organisasiResp] = useAxios({ method: "GET", url: "/references/organisasi", auth: true });
  const [jenisKelaminResp] = useAxios({ method: "GET", url: "/references/jenis-kelamin", auth: true });
  const [agamaResp] = useAxios({ method: "GET", url: "/references/agama", auth: true });
  const [statusPerkawinanResp] = useAxios({
    method: "GET",
    url: "/references/status-perkawinan",
    auth: true,
  });
  const [pekerjaanResp] = useAxios({ method: "GET", url: "/references/pekerjaan", auth: true });
  const [sumberPendapatanResp] = useAxios({
    method: "GET",
    url: "/references/sumber-pendapatan",
    auth: true,
  });

  const groupName = String(user?.groupname || "").toUpperCase();
  const isAdminKabKota = groupName === "ADMIN_KONI_KABKOTA";
  const isAdminKoni = ["ADMIN_KONI_KABKOTA", "ADMIN_KONI_PROVINSI"].includes(groupName);
  const isAdminCabor = groupName === "ADMIN_CABOR";
  const isAdminKlub = groupName === "ADMIN_KLUB";
  const showKoniKabKotaFilter = ["SUPERADMIN", "ADMIN_KONI_PROVINSI"].includes(groupName);
	const isContextFixed = isAdminKlub;
	const isCompactOrganizationSelector = ["ADMIN_KONI_KABKOTA", "ADMIN_CABOR"].includes(groupName);
	const canManage = hasPermission(user, "personal.manage");
	const canDelete = canManage && !["ADMIN_CABOR", "ADMIN_KLUB"].includes(groupName);
	const url = `/sdm/${rolePath}`;
  const caborOptions = useMemo(() => toOptions(caborResp?.data, "id_cabor"), [caborResp]);
  const caborIndukOptions = useMemo(
    () => toOptions(caborIndukResp?.data, "id_cabor_induk", "nama"),
    [caborIndukResp],
  );
  const klubOptionsBase = useMemo(
    () => {
      const rows = (Array.isArray(organisasiResp?.data) ? organisasiResp.data : []).filter(
        (item) => String(item?.jenis_kode || "").toUpperCase() === "KLUB",
      );
      const selectedKlubCaborID = isAdminKabKota ? selectedCaborInduk.id_cabor : selectedCaborID;
      const filteredRows =
        (isAdminKabKota || isAdminCabor) && selectedKlubCaborID
          ? rows.filter((item) => {
              const itemCaborIDs = String(item?.id_cabor || "")
                .split(",")
                .map((id) => id.trim())
                .filter(Boolean);
              const matchCabor = itemCaborIDs.includes(selectedKlubCaborID);
              if (!isAdminKabKota) return matchCabor;
              return (
                String(item?.id_organisasi_induk || "") === selectedCaborInduk.id_induk_organisasi &&
                matchCabor
              );
            })
          : rows;
      return toOptions(filteredRows, "id_organisasi", (item) => item?.nama || "");
    },
    [organisasiResp, isAdminKabKota, isAdminCabor, selectedCaborInduk, selectedCaborID],
  );
  const klubOptions = useMemo(() => {
    const currentID = String(currentEditKlubOption?.id_organisasi || "").trim();
    if (!currentID) return klubOptionsBase;
    const exists = klubOptionsBase.some((item) => String(item?.id_organisasi || "") === currentID);
    if (exists) return klubOptionsBase;
    return [
      {
        ...currentEditKlubOption,
        __value: currentID,
        __label: currentEditKlubOption?.nama || currentEditKlubOption?.__label || "",
      },
      ...klubOptionsBase,
    ];
  }, [klubOptionsBase, currentEditKlubOption]);
  const koniKabKotaOptions = useMemo(
    () =>
      toOptions(
        (Array.isArray(organisasiResp?.data) ? organisasiResp.data : []).filter(
          (item) =>
            String(item?.jenis_kode || "").toUpperCase() === "KONI" &&
            String(item?.tingkat_kode || "").toUpperCase() === "KABUPATEN_KOTA",
        ),
        "id_organisasi",
        "nama",
      ),
    [organisasiResp],
  );
  const jenisKelaminOptions = useMemo(
    () => toOptions(jenisKelaminResp?.data, "id_jenis_kelamin"),
    [jenisKelaminResp],
  );
  const agamaOptions = useMemo(() => toOptions(agamaResp?.data, "id_agama"), [agamaResp]);
  const statusPerkawinanOptions = useMemo(
    () => toOptions(statusPerkawinanResp?.data, "id_status_perkawinan"),
    [statusPerkawinanResp],
  );
  const pekerjaanOptions = useMemo(
    () => (Array.isArray(pekerjaanResp?.data) ? pekerjaanResp.data : []),
    [pekerjaanResp],
  );
  const sumberPendapatanOptions = useMemo(
    () => (Array.isArray(sumberPendapatanResp?.data) ? sumberPendapatanResp.data : []),
    [sumberPendapatanResp],
  );
  const golonganDarahOptions = [
    { id: "A", nama: "A" },
    { id: "B", nama: "B" },
    { id: "AB", nama: "AB" },
    { id: "O", nama: "O" },
  ];
  const statusBPJSOptions = [
    { id: "Aktif", nama: "Aktif" },
    { id: "Nonaktif", nama: "Nonaktif" },
  ];
  const statusAktifOptions = [
    { id: "true", nama: "Aktif" },
    { id: "false", nama: "Nonaktif" },
  ];
  const visibleListColumns = [
    "no",
    "nik",
    "nomor_induk",
    "nama_lengkap",
    "jenis_kelamin_code",
    "usia",
    "cabor_name",
    "klub_olahraga_name",
    "aktif",
  ];
  const sdmExtraDataColumns = [
    "no_kk",
    "gelar_depan",
    "gelar_belakang",
    "tempat_lahir",
    "tanggal_lahir",
    "nomor_akta_lahir",
    "tahun_akta_lahir",
    "jenis_kelamin_name",
    "golongan_darah",
    "agama_name",
    "kewarganegaraan",
    "status_perkawinan_name",
    "hp",
    "telepon",
    "email",
    "alamat",
    "kota",
    "kode_pos",
    "pekerjaan",
    "jabatan",
    "bidang_usaha",
    "nama_tempat_kerja",
    "telepon_tempat_kerja",
    "alamat_tempat_kerja",
    "pendapatan",
    "sumber_pendapatan",
    "npwp",
    "nama_ayah",
    "pekerjaan_ayah",
    "nama_ibu",
    "pekerjaan_ibu",
    "alamat_orang_tua",
    "nomor_rekening",
    "rekening_atas_nama",
    "bank",
    "nomor_bpjs_kesehatan",
    "status_bpjs_kesehatan",
    "nomor_bpjs_ketenagakerjaan",
    "status_bpjs_ketenagakerjaan",
    "asuransi_lainnya",
    "nomor_asuransi_lainnya",
    "tanggal_mulai",
  ];
  const detailColumns = [
    "cabor_name",
    "organisasi_name",
    "nik",
    "nomor_induk",
    "no_kk",
    "nama_lengkap",
    "gelar_depan",
    "gelar_belakang",
    "tempat_lahir",
    "tanggal_lahir",
    "nomor_akta_lahir",
    "tahun_akta_lahir",
    "jenis_kelamin_name",
    "golongan_darah",
    "agama_name",
    "kewarganegaraan",
    "status_perkawinan_name",
    "hp",
    "telepon",
    "email",
    "alamat",
    "kota",
    "kode_pos",
    "div_pekerjaan",
    "pekerjaan",
    "jabatan",
    "bidang_usaha",
    "nama_tempat_kerja",
    "telepon_tempat_kerja",
    "alamat_tempat_kerja",
    "pendapatan",
    "sumber_pendapatan",
    "npwp",
    "div_orang_tua",
    "nama_ayah",
    "pekerjaan_ayah",
    "nama_ibu",
    "pekerjaan_ibu",
    "alamat_orang_tua",
    "div_rekening",
    "nomor_rekening",
    "rekening_atas_nama",
    "bank",
    "div_bpjs",
    "nomor_bpjs_kesehatan",
    "status_bpjs_kesehatan",
    "nomor_bpjs_ketenagakerjaan",
    "status_bpjs_ketenagakerjaan",
    "asuransi_lainnya",
    "nomor_asuransi_lainnya",
    "tanggal_mulai",
    "aktif",
    "photo_url",
  ];
  const personalLanjutanFields = [
    "photo_url",
    "pekerjaan",
    "jabatan",
    "bidang_usaha",
    "nama_tempat_kerja",
    "telepon_tempat_kerja",
    "alamat_tempat_kerja",
    "pendapatan",
    "sumber_pendapatan",
    "npwp",
    "nama_ayah",
    "pekerjaan_ayah",
    "nama_ibu",
    "pekerjaan_ibu",
    "alamat_orang_tua",
    "nomor_rekening",
    "rekening_atas_nama",
    "bank",
    "nomor_bpjs_kesehatan",
    "status_bpjs_kesehatan",
    "nomor_bpjs_ketenagakerjaan",
    "status_bpjs_ketenagakerjaan",
    "asuransi_lainnya",
    "nomor_asuransi_lainnya",
  ];
  const personalLookupFields = [
    "id_personal",
    "no_kk",
    "gelar_depan",
    "nama_lengkap",
    "gelar_belakang",
    "tempat_lahir",
    "tanggal_lahir",
    "nomor_akta_lahir",
    "tahun_akta_lahir",
    "id_jenis_kelamin",
    "golongan_darah",
    "id_agama",
    "kewarganegaraan",
    "id_status_perkawinan",
    "hp",
    "telepon",
    "email",
    "alamat",
    "kota",
    "kode_pos",
    "file_kartu_keluarga",
    "file_ktp",
    "file_akta_lahir",
    "file_rekening_bank",
    "photo_url",
    ...personalLanjutanFields,
  ];
  const addLockedFields = [
    "nomor_induk",
    "no_kk",
    "nama_lengkap",
    "gelar_depan",
    "gelar_belakang",
    "tempat_lahir",
    "tanggal_lahir",
    "nomor_akta_lahir",
    "tahun_akta_lahir",
    "id_jenis_kelamin",
    "golongan_darah",
    "id_agama",
    "kewarganegaraan",
    "id_status_perkawinan",
    "hp",
    "telepon",
    "email",
    "alamat",
    "kota",
    "kode_pos",
    "file_kartu_keluarga",
    "file_ktp",
    "file_akta_lahir",
    "file_rekening_bank",
    ...personalLanjutanFields,
    "tanggal_mulai",
    "catatan",
  ];

  const coltype = {
    id_keanggotaan: { type: "hidden", name: "id_keanggotaan" },
    id_personal: { type: "hidden", name: "id_personal" },
    id_induk_organisasi: { type: "hidden", name: "id_induk_organisasi" },
    no_kk: {
      label: "No. KK",
      type: "text",
      name: "no_kk",
      colsize: 3,
      validation: RuleRequired,
      width: 130,
      align: "center",
    },
    photo_url: {
      label: "Foto",
      type: "photodrop",
      name: "photo_url",
      colsize: 12,
      url: "/file/upload",
      data: { location: "personal" },
      allowedExtentions: ["jpg", "jpeg", "png"],
      caption: "Format jpg, jpeg, atau png.",
      renderExportFunc: (val) => showEmpty(val),
    },
    file_kartu_keluarga: {
      label: "Kartu Keluarga",
      type: "filedrop",
      name: "file_kartu_keluarga",
      colsize: 6,
      url: "/file/upload",
      data: { location: "personal-document" },
      allowedExtentions: ["jpg", "jpeg", "png", "pdf"],
      validation: isAdminKoni ? undefined : RuleRequired,
      renderExportFunc: (val) => showEmpty(val),
    },
    file_ktp: {
      label: "KTP",
      type: "filedrop",
      name: "file_ktp",
      colsize: 6,
      url: "/file/upload",
      data: { location: "personal-document" },
      allowedExtentions: ["jpg", "jpeg", "png", "pdf"],
      renderExportFunc: (val) => showEmpty(val),
    },
    file_akta_lahir: {
      label: "Akta Kelahiran",
      type: "filedrop",
      name: "file_akta_lahir",
      colsize: 6,
      url: "/file/upload",
      data: { location: "personal-document" },
      allowedExtentions: ["jpg", "jpeg", "png", "pdf"],
      renderExportFunc: (val) => showEmpty(val),
    },
    file_rekening_bank: {
      label: "Rekening Bank",
      type: "filedrop",
      name: "file_rekening_bank",
      colsize: 6,
      url: "/file/upload",
      data: { location: "personal-document" },
      allowedExtentions: ["jpg", "jpeg", "png", "pdf"],
      renderExportFunc: (val) => showEmpty(val),
    },
    nik: {
      label: "NIK",
      type: "text",
      name: "nik",
      colsize: 3,
      validation: RuleRequired,
      width: 130,
      align: "center",
    },
    gelar_depan: {
      label: "Gelar Depan",
      type: "text",
      name: "gelar_depan",
      colsize: 3,
      width: 110,
    },
    nama_lengkap: {
      label: "Nama",
      type: "text",
      name: "nama_lengkap",
      colsize: 3,
      validation: RuleRequired,
      width: 220,
      pdfwidth: 16,
      renderFunc: (_value, _column, item) => formatFullName(item),
      renderExportFunc: (_value, _column, item) => formatFullName(item),
    },
    gelar_belakang: {
      label: "Gelar Belakang",
      type: "text",
      name: "gelar_belakang",
      colsize: 3,
      width: 120,
    },
    tempat_lahir: {
      label: "Tempat Lahir",
      type: "text",
      name: "tempat_lahir",
      colsize: 3,
      validation: RuleRequired,
      width: 130,
    },
    tanggal_lahir: {
      label: "Tanggal Lahir",
      type: "date",
      name: "tanggal_lahir",
      colsize: 3,
      validation: RuleRequired,
      width: 110,
      align: "center",
    },
    nomor_akta_lahir: {
      label: "Nomor Akta Lahir",
      type: "text",
      name: "nomor_akta_lahir",
      colsize: 3,
      width: 150,
    },
    tahun_akta_lahir: {
      label: "Tahun Akta Lahir",
      type: "number",
      name: "tahun_akta_lahir",
      colsize: 3,
      width: 120,
    },
    id_jenis_kelamin: {
      label: "Jenis Kelamin",
      type: "dropdown",
      name: "id_jenis_kelamin",
      colsize: 3,
      options: jenisKelaminOptions,
      valueId: "id_jenis_kelamin",
      labelId: "nama",
      placeholder: "-Pilih Jenis Kelamin-",
      validation: RuleRequired,
      width: 110,
    },
    golongan_darah: {
      label: "Golongan Darah",
      type: "dropdown",
      name: "golongan_darah",
      colsize: 3,
      options: golonganDarahOptions,
      valueId: "id",
      labelId: "nama",
      placeholder: "-Pilih Golongan Darah-",
      validation: RuleRequired,
      width: 100,
    },
    id_agama: {
      label: "Agama",
      type: "dropdown",
      name: "id_agama",
      colsize: 3,
      options: agamaOptions,
      valueId: "id_agama",
      labelId: "nama",
      placeholder: "-Pilih Agama-",
      validation: RuleRequired,
      width: 120,
    },
    agama_name: {
      label: "Agama",
      renderFunc: (val) => showEmpty(val),
      width: 120,
    },
    kewarganegaraan: {
      label: "Kewarganegaraan",
      type: "text",
      name: "kewarganegaraan",
      colsize: 3,
      validation: RuleRequired,
      width: 130,
    },
    id_status_perkawinan: {
      label: "Status Perkawinan",
      type: "dropdown",
      name: "id_status_perkawinan",
      colsize: 3,
      options: statusPerkawinanOptions,
      valueId: "id_status_perkawinan",
      labelId: "nama",
      placeholder: "-Pilih Status Perkawinan-",
      validation: RuleRequired,
      width: 140,
    },
    status_perkawinan_name: {
      label: "Status Perkawinan",
      renderFunc: (val) => showEmpty(val),
      width: 140,
    },
    jenis_kelamin_code: {
      label: "JK",
      renderFunc: (val) => showEmpty(val),
      width: 60,
      pdfwidth: 4,
      align: "center",
    },
    usia: {
      label: "Usia",
      renderFunc: (val) => showEmpty(val),
      width: 70,
      pdfwidth: 4,
      align: "center",
    },
    jenis_kelamin_name: {
      label: "Jenis Kelamin",
      renderFunc: (val) => showEmpty(val),
      width: 110,
    },
    hp: {
      label: "HP",
      type: "text",
      name: "hp",
      colsize: 3,
      validation: RuleRequired,
      width: 120,
      pdfwidth: 9,
      align: "center",
    },
    telepon: {
      label: "Telepon",
      type: "text",
      name: "telepon",
      colsize: 3,
      width: 120,
    },
    email: {
      label: "Email",
      type: "email",
      name: "email",
      colsize: 3,
      validation: RuleRequired,
      width: 180,
      pdfwidth: 13,
    },
    alamat: {
      label: "Alamat",
      type: "textarea",
      name: "alamat",
      colsize: 6,
      validation: RuleRequired,
      width: 220,
    },
    kota: {
      label: "Kab/Kota",
      type: "text",
      name: "kota",
      colsize: 3,
      validation: RuleRequired,
      width: 140,
    },
    kode_pos: {
      label: "Kode POS",
      type: "text",
      name: "kode_pos",
      colsize: 3,
      validation: RuleRequired,
      width: 90,
    },
    div_pekerjaan: { type: "divider", label: "Data Pekerjaan" },
    pekerjaan: {
      label: "Pekerjaan",
      type: "dropdowndinamis",
      name: "pekerjaan",
      colsize: 3,
      options: pekerjaanOptions,
      valueId: "nama",
      labelId: "nama",
      placeholder: "-Pilih Pekerjaan-",
      width: 150,
      add_config: pekerjaanAddConfig("Tambah Pekerjaan"),
    },
    jabatan: {
      label: "Jabatan di Tempat Kerja",
      type: "text",
      name: "jabatan",
      colsize: 3,
      width: 160,
    },
    bidang_usaha: {
      label: "Bidang Usaha Tempat Kerja",
      type: "text",
      name: "bidang_usaha",
      colsize: 3,
      width: 170,
    },
    nama_tempat_kerja: {
      label: "Nama Tempat Kerja",
      type: "text",
      name: "nama_tempat_kerja",
      colsize: 3,
      width: 180,
    },
    telepon_tempat_kerja: {
      label: "Telepon Tempat Kerja",
      type: "text",
      name: "telepon_tempat_kerja",
      colsize: 3,
      width: 150,
    },
    alamat_tempat_kerja: {
      label: "Alamat Tempat Kerja",
      type: "textarea",
      name: "alamat_tempat_kerja",
      colsize: 6,
      width: 220,
    },
    pendapatan: {
      label: "Pendapatan",
      type: "number",
      name: "pendapatan",
      colsize: 3,
      width: 130,
      renderExportFunc: (val) => formatWholeNumber(val),
    },
    sumber_pendapatan: {
      label: "Sumber Pendapatan",
      type: "dropdowndinamis",
      name: "sumber_pendapatan",
      colsize: 3,
      options: sumberPendapatanOptions,
      valueId: "nama",
      labelId: "nama",
      placeholder: "-Pilih Sumber Pendapatan-",
      width: 170,
      add_config: {
        title: "Tambah Sumber Pendapatan",
        url: "/references/sumber-pendapatan",
        cellid: "id_sumber_pendapatan",
        columns: ["nama"],
        coltype: {
          nama: {
            type: "text",
            label: "Nama Sumber Pendapatan",
            name: "nama",
            validation: RuleRequired,
            colsize: 12,
          },
        },
        optionsUrl: "/references/sumber-pendapatan",
        optionsAuth: true,
        otherLabel: "Lainnya",
      },
    },
    npwp: {
      label: "NPWP",
      type: "text",
      name: "npwp",
      colsize: 3,
      width: 140,
    },
    div_orang_tua: { type: "divider", label: "Data Orang Tua" },
    nama_ayah: {
      label: "Nama Ayah",
      type: "text",
      name: "nama_ayah",
      colsize: 3,
      validation: RuleRequired,
      width: 170,
    },
    pekerjaan_ayah: {
      label: "Pekerjaan Ayah",
      type: "dropdowndinamis",
      name: "pekerjaan_ayah",
      colsize: 3,
      validation: RuleRequired,
      options: pekerjaanOptions,
      valueId: "nama",
      labelId: "nama",
      placeholder: "-Pilih Pekerjaan Ayah-",
      add_config: pekerjaanAddConfig("Tambah Pekerjaan Ayah"),
      width: 150,
    },
    nama_ibu: {
      label: "Nama Ibu",
      type: "text",
      name: "nama_ibu",
      colsize: 3,
      validation: RuleRequired,
      width: 170,
    },
    pekerjaan_ibu: {
      label: "Pekerjaan Ibu",
      type: "dropdowndinamis",
      name: "pekerjaan_ibu",
      colsize: 3,
      validation: RuleRequired,
      options: pekerjaanOptions,
      valueId: "nama",
      labelId: "nama",
      placeholder: "-Pilih Pekerjaan Ibu-",
      add_config: pekerjaanAddConfig("Tambah Pekerjaan Ibu"),
      width: 150,
    },
    alamat_orang_tua: {
      label: "Alamat Orang Tua",
      type: "textarea",
      name: "alamat_orang_tua",
      colsize: 12,
      validation: RuleRequired,
      width: 220,
    },
    div_rekening: { type: "divider", label: "Rekening Bank" },
    nomor_rekening: {
      label: "Nomor Rekening",
      type: "text",
      name: "nomor_rekening",
      colsize: 4,
      width: 150,
    },
    rekening_atas_nama: {
      label: "Rekening Atas Nama",
      type: "text",
      name: "rekening_atas_nama",
      colsize: 4,
      width: 170,
    },
    bank: {
      label: "Bank",
      type: "text",
      name: "bank",
      colsize: 4,
      width: 130,
    },
    div_bpjs: { type: "divider", label: "BPJS dan Asuransi" },
    nomor_bpjs_kesehatan: {
      label: "No. BPJS Kesehatan",
      type: "text",
      name: "nomor_bpjs_kesehatan",
      colsize: 3,
      width: 160,
    },
    status_bpjs_kesehatan: {
      label: "Status BPJS Kesehatan",
      type: "dropdown",
      name: "status_bpjs_kesehatan",
      colsize: 3,
      options: statusBPJSOptions,
      valueId: "id",
      labelId: "nama",
      placeholder: "-Pilih Status-",
      width: 150,
    },
    nomor_bpjs_ketenagakerjaan: {
      label: "No. BPJS Ketenagakerjaan",
      type: "text",
      name: "nomor_bpjs_ketenagakerjaan",
      colsize: 3,
      width: 180,
    },
    status_bpjs_ketenagakerjaan: {
      label: "Status BPJS Ketenagakerjaan",
      type: "dropdown",
      name: "status_bpjs_ketenagakerjaan",
      colsize: 3,
      options: statusBPJSOptions,
      valueId: "id",
      labelId: "nama",
      placeholder: "-Pilih Status-",
      width: 170,
    },
    asuransi_lainnya: {
      label: "Asuransi Lainnya",
      type: "text",
      name: "asuransi_lainnya",
      colsize: 6,
      width: 170,
    },
    nomor_asuransi_lainnya: {
      label: "Nomor Asuransi Lainnya",
      type: "text",
      name: "nomor_asuransi_lainnya",
      colsize: 6,
      width: 180,
    },
    id_cabor: {
      label: "Cabor",
      type: "dropdown",
      name: "id_cabor",
      colsize: isCompactOrganizationSelector ? 3 : 6,
      validation: RuleRequired,
      options: caborOptions,
      valueId: "id_cabor",
      labelId: "__label",
      placeholder: "-Pilih Cabor-",
      width: 150,
    },
    id_cabor_induk: {
      label: "Cabor/Induk Organisasi",
      type: "dropdown",
      name: "id_cabor_induk",
      colsize: 3,
      validation: RuleRequired,
      options: caborIndukOptions,
      valueId: "id_cabor_induk",
      labelId: "__label",
      placeholder: "-Pilih Cabor/Induk Organisasi-",
      width: 180,
    },
    cabor_name: {
      label: "Cabor",
      renderFunc: (val) => showEmpty(val),
      width: 150,
      pdfwidth: 13,
    },
    id_organisasi: {
      label: "Klub Olahraga",
      type: "dropdown",
      name: "id_organisasi",
      colsize: isCompactOrganizationSelector ? 3 : 6,
      validation: isContextFixed || isAdminKabKota || isAdminCabor ? undefined : RuleRequired,
      options: klubOptions,
      valueId: "id_organisasi",
      labelId: "__label",
      placeholder: "-Pilih Klub Olahraga-",
      width: 180,
    },
    organisasi_name: {
      label: "Klub Olahraga",
      renderFunc: (val) => showEmpty(val),
      width: 180,
      pdfwidth: 15,
    },
    klub_olahraga_name: {
      label: "Klub Olahraga",
      renderFunc: (val) => showEmpty(val),
      renderExportFunc: (val) => showEmpty(val),
      width: 180,
      pdfwidth: 15,
    },
    nomor_induk: {
      label: "No. Induk",
      type: "text",
      name: "nomor_induk",
      colsize: 3,
      width: 130,
      pdfwidth: 8,
      align: "center",
      disabled: true,
    },
    tanggal_mulai: {
      label: `Tanggal Mulai Menjadi ${title}`,
      type: "date",
      name: "tanggal_mulai",
      colsize: 3,
      validation: RuleRequired,
      width: 110,
      align: "center",
    },
    catatan: {
      label: "Catatan",
      type: "textarea",
      name: "catatan",
      colsize: 12,
      width: 220,
    },
    aktif: {
      label: "Status",
      type: "checkbox",
      name: "aktif",
      renderFunc: (val) => boolText(val),
      width: 90,
      pdfwidth: 5,
      align: "center",
    },
    action: { label: "Aksi", align: "center", width: 120 },
  };

  const baseColumnsForm = [
    ...(isAdminKabKota ? ["id_cabor_induk", "id_cabor", "id_induk_organisasi"] : ["id_cabor"]),
    ...(isContextFixed ? [] : ["id_organisasi"]),
    "nik",
    "nomor_induk",
    "no_kk",
    "nama_lengkap",
    "gelar_depan",
    "gelar_belakang",
    "tempat_lahir",
    "tanggal_lahir",
    "nomor_akta_lahir",
    "tahun_akta_lahir",
    "id_jenis_kelamin",
    "golongan_darah",
    "id_agama",
    "kewarganegaraan",
    "id_status_perkawinan",
    "hp",
    "telepon",
    "email",
    "alamat",
    "kota",
    "kode_pos",
    "div_pekerjaan",
    "pekerjaan",
    "jabatan",
    "bidang_usaha",
    "nama_tempat_kerja",
    "telepon_tempat_kerja",
    "alamat_tempat_kerja",
    "pendapatan",
    "sumber_pendapatan",
    "npwp",
    "div_orang_tua",
    "nama_ayah",
    "pekerjaan_ayah",
    "nama_ibu",
    "pekerjaan_ibu",
    "alamat_orang_tua",
    "div_rekening",
    "nomor_rekening",
    "rekening_atas_nama",
    "bank",
    "div_bpjs",
    "nomor_bpjs_kesehatan",
    "status_bpjs_kesehatan",
    "nomor_bpjs_ketenagakerjaan",
    "status_bpjs_ketenagakerjaan",
    "asuransi_lainnya",
    "nomor_asuransi_lainnya",
    "tanggal_mulai",
    "photo_url",
    "file_kartu_keluarga",
    "file_ktp",
    "file_akta_lahir",
    "file_rekening_bank",
  ];
  const editColumnsForm = [
    ...(isAdminKabKota ? ["id_induk_organisasi"] : []),
    "id_cabor",
    ...(isContextFixed ? [] : ["id_organisasi"]),
    "nik",
    "nomor_induk",
    "no_kk",
    "nama_lengkap",
    "gelar_depan",
    "gelar_belakang",
    "tempat_lahir",
    "tanggal_lahir",
    "nomor_akta_lahir",
    "tahun_akta_lahir",
    "id_jenis_kelamin",
    "golongan_darah",
    "id_agama",
    "kewarganegaraan",
    "id_status_perkawinan",
    "hp",
    "telepon",
    "email",
    "alamat",
    "kota",
    "kode_pos",
    "div_pekerjaan",
    "pekerjaan",
    "jabatan",
    "bidang_usaha",
    "nama_tempat_kerja",
    "telepon_tempat_kerja",
    "alamat_tempat_kerja",
    "pendapatan",
    "sumber_pendapatan",
    "npwp",
    "div_orang_tua",
    "nama_ayah",
    "pekerjaan_ayah",
    "nama_ibu",
    "pekerjaan_ibu",
    "alamat_orang_tua",
    "div_rekening",
    "nomor_rekening",
    "rekening_atas_nama",
    "bank",
    "div_bpjs",
    "nomor_bpjs_kesehatan",
    "status_bpjs_kesehatan",
    "nomor_bpjs_ketenagakerjaan",
    "status_bpjs_ketenagakerjaan",
    "asuransi_lainnya",
    "nomor_asuransi_lainnya",
    "tanggal_mulai",
    "photo_url",
    "file_kartu_keluarga",
    "file_ktp",
    "file_akta_lahir",
    "file_rekening_bank",
  ];
  const isAddReadyToSave = addLookup.status === "new" || addLookup.status === "available";
  const editableLookupUploadFields = [
    "photo_url",
    "file_kartu_keluarga",
    "file_ktp",
    "file_akta_lahir",
    "file_rekening_bank",
  ];
  const addColtype = useMemo(() => {
    const locked = !isAddReadyToSave;
    const existingPersonalLocked = addLookup.status === "available";
    return Object.keys(coltype).reduce((result, key) => {
      const item = coltype[key];
      const shouldDisable =
        key === "nomor_induk" ||
        (locked && addLockedFields.includes(key)) ||
        (existingPersonalLocked &&
          personalLookupFields.includes(key) &&
          !editableLookupUploadFields.includes(key));
      const addItem = isAdminKabKota && key === "id_cabor" ? { ...item, type: "hidden" } : item;
      result[key] = shouldDisable ? { ...addItem, disabled: true } : addItem;
      return result;
    }, {});
  }, [addLookup.status, coltype, editableLookupUploadFields, isAdminKabKota, isAddReadyToSave]);
  const editColtype = useMemo(() => {
    return Object.keys(coltype).reduce((result, key) => {
      const item = coltype[key];
      const shouldDisable = ["id_cabor", "id_cabor_induk", "nik", "nomor_induk"].includes(key);
      result[key] = shouldDisable ? { ...item, disabled: true } : item;
      return result;
    }, {});
  }, [coltype]);

  const resetAddLookup = () => {
    lookupKeyRef.current = "";
    if (lookupTimerRef.current) {
      clearTimeout(lookupTimerRef.current);
      lookupTimerRef.current = null;
    }
    setAddLookup({
      status: "idle",
      exists: false,
      registered: false,
      message: "Isi Cabor dan NIK 16 digit untuk memulai pengecekan data.",
    });
    setSelectedCaborInduk({ id_cabor: "", id_induk_organisasi: "" });
    setSelectedCaborID("");
    setCurrentEditKlubOption(null);
  };

  const clearPersonalLookupFields = (setValue) => {
    personalLookupFields.forEach((field) => setValue(field, ""));
    setValue("nomor_induk", "");
  };

  const applyLookupResult = (setValue, result) => {
    const personal = result?.personal || {};
    personalLookupFields.forEach((field) => {
      if (Object.prototype.hasOwnProperty.call(personal, field)) {
        setValue(field, personal[field] ?? "");
      }
    });

    if (result?.registered) {
      const membership = result?.membership || {};
      setValue("nomor_induk", membership?.nomor_induk || "");
      setValue("tanggal_mulai", membership?.tanggal_mulai || "");
      setValue("catatan", membership?.catatan || "");
      setAddLookup({
        status: "registered",
        exists: true,
        registered: true,
        message: "NIK ini sudah terdaftar pada cabor terpilih.",
      });
      return;
    }

    setValue("nomor_induk", "");
    setAddLookup({
      status: "available",
      exists: true,
      registered: false,
      message: "Data personal ditemukan. Data personal tidak dapat diubah, silakan simpan keanggotaannya.",
    });
  };

  const onAddFormChange = (setValue, form) => {
    if (currentEditKlubOption) {
      setCurrentEditKlubOption(null);
    }
    let effectiveCaborID = String(form?.id_cabor || "").trim();
    let effectiveIndukID = String(form?.id_induk_organisasi || "").trim();

    if (isAdminKabKota) {
      const selectedKey = String(form?.id_cabor_induk || "").trim();
      const selectedOption = caborIndukOptions.find((item) => String(item?.id_cabor_induk || "") === selectedKey);
      const nextCaborID = selectedOption?.id_cabor ? String(selectedOption.id_cabor) : "";
      const nextIndukID = selectedOption?.id_induk_organisasi
        ? String(selectedOption.id_induk_organisasi)
        : "";
      effectiveCaborID = nextCaborID;
      effectiveIndukID = nextIndukID;

      if (
        nextCaborID !== String(form?.id_cabor || "") ||
        nextIndukID !== String(form?.id_induk_organisasi || "")
      ) {
        setValue("id_cabor", nextCaborID);
        setValue("id_induk_organisasi", nextIndukID);
        setValue("id_organisasi", "");
      }
      if (
        selectedCaborInduk.id_cabor !== nextCaborID ||
        selectedCaborInduk.id_induk_organisasi !== nextIndukID
      ) {
        setSelectedCaborInduk({
          id_cabor: nextCaborID,
          id_induk_organisasi: nextIndukID,
        });
      }
    }
    if (!isAdminKabKota && selectedCaborID !== effectiveCaborID) {
      setSelectedCaborID(effectiveCaborID);
      setValue("id_organisasi", "");
    }

    const nikRaw = String(form?.nik || "");
    const nik = nikRaw.replace(/\D/g, "").slice(0, 16);
    if (nikRaw !== nik) {
      setValue("nik", nik);
      return;
    }

    const idCabor = effectiveCaborID;
    const idOrganisasi = String(form?.id_organisasi || "").trim();
    const idIndukOrganisasi = effectiveIndukID;
    const key = `${rolePath}|${idCabor}|${idIndukOrganisasi}|${idOrganisasi}|${nik}`;

    if (nik.length < 16 || !idCabor) {
      if (lookupKeyRef.current !== key) {
        lookupKeyRef.current = key;
        clearPersonalLookupFields(setValue);
        setAddLookup({
          status: "idle",
          exists: false,
          registered: false,
          message: "Isi Cabor dan NIK 16 digit untuk memulai pengecekan data.",
        });
      }
      return;
    }

    if (lookupKeyRef.current === key) return;
    lookupKeyRef.current = key;
    if (lookupTimerRef.current) {
      clearTimeout(lookupTimerRef.current);
    }
    setAddLookup({
      status: "checking",
      exists: false,
      registered: false,
      message: "Sedang mengecek NIK.",
    });
    lookupTimerRef.current = setTimeout(async () => {
      const result = await axiosFetch({
        method: "GET",
        url: `/sdm-lookup/${rolePath}/nik`,
        auth: true,
        params: {
          nik,
          id_cabor: idCabor,
          id_induk_organisasi: idIndukOrganisasi,
          id_organisasi: idOrganisasi,
        },
      });

      if (
        result?.data?.success === false ||
        !Object.prototype.hasOwnProperty.call(result?.data || {}, "exists")
      ) {
        clearPersonalLookupFields(setValue);
        setValue("nik", nik);
        setAddLookup({
          status: "idle",
          exists: false,
          registered: false,
          message: result?.message || "Pengecekan NIK gagal dilakukan.",
        });
        return;
      }

      if (result?.data?.exists) {
        applyLookupResult(setValue, result.data);
        return;
      }

      clearPersonalLookupFields(setValue);
      setValue("nik", nik);
      setAddLookup({
        status: "new",
        exists: false,
        registered: false,
        message: "NIK belum tersedia. Silakan lengkapi data baru.",
      });
    }, 350);
  };

  const onEditFormChange = (_setValue, form) => {
    const nextCaborID = String(form?.id_cabor || "").trim();
    const nextIndukID = String(form?.id_induk_organisasi || "").trim();
    const nextKlubID = String(form?.id_organisasi || "").trim();
    if (nextKlubID) {
      const nextKlubName = String(form?.organisasi_name || form?.klub_olahraga_name || "").trim();
      if (String(currentEditKlubOption?.id_organisasi || "") !== nextKlubID) {
        setCurrentEditKlubOption({
          id_organisasi: nextKlubID,
          nama: nextKlubName,
          __value: nextKlubID,
          __label: nextKlubName,
        });
      }
    }

    if (isAdminKabKota) {
      const nextSelection = {
        id_cabor: nextCaborID,
        id_induk_organisasi: nextIndukID,
      };
      if (
        selectedCaborInduk.id_cabor !== nextSelection.id_cabor ||
        selectedCaborInduk.id_induk_organisasi !== nextSelection.id_induk_organisasi
      ) {
        setSelectedCaborInduk(nextSelection);
      }
      return;
    }

    if (selectedCaborID !== nextCaborID) {
      setSelectedCaborID(nextCaborID);
    }
  };

  const saveHandle = async (form, defaultValue = {}) => {
    const id = form?.id_keanggotaan || defaultValue?.id_keanggotaan || "";
    return axiosFetch({
      method: id ? "PUT" : "POST",
      url: id ? `${url}/${id}` : url,
      auth: true,
      data: {
        no_kk: form?.no_kk || defaultValue?.no_kk || "",
        nik: form?.nik || defaultValue?.nik || "",
        gelar_depan: form?.gelar_depan || "",
        nama_lengkap: form?.nama_lengkap || "",
        gelar_belakang: form?.gelar_belakang || "",
        tempat_lahir: form?.tempat_lahir || "",
        tanggal_lahir: form?.tanggal_lahir || "",
        nomor_akta_lahir: form?.nomor_akta_lahir || "",
        tahun_akta_lahir: form?.tahun_akta_lahir || "",
        id_jenis_kelamin: Number(form?.id_jenis_kelamin || 0),
        golongan_darah: form?.golongan_darah || "",
        id_agama: Number(form?.id_agama || 0),
        kewarganegaraan: form?.kewarganegaraan || "",
        id_status_perkawinan: Number(form?.id_status_perkawinan || 0),
        hp: form?.hp || "",
        telepon: form?.telepon || "",
        email: form?.email || "",
        alamat: form?.alamat || "",
        kota: form?.kota || "",
        kode_pos: form?.kode_pos || "",
        photo_url: form?.photo_url || "",
        pekerjaan: form?.pekerjaan || "",
        jabatan: form?.jabatan || "",
        bidang_usaha: form?.bidang_usaha || "",
        nama_tempat_kerja: form?.nama_tempat_kerja || "",
        telepon_tempat_kerja: form?.telepon_tempat_kerja || "",
        alamat_tempat_kerja: form?.alamat_tempat_kerja || "",
        pendapatan: form?.pendapatan || "",
        sumber_pendapatan: form?.sumber_pendapatan || "",
        npwp: form?.npwp || "",
        nama_ayah: form?.nama_ayah || "",
        pekerjaan_ayah: form?.pekerjaan_ayah || "",
        nama_ibu: form?.nama_ibu || "",
        pekerjaan_ibu: form?.pekerjaan_ibu || "",
        alamat_orang_tua: form?.alamat_orang_tua || "",
        nomor_rekening: form?.nomor_rekening || "",
        rekening_atas_nama: form?.rekening_atas_nama || "",
        bank: form?.bank || "",
        nomor_bpjs_kesehatan: form?.nomor_bpjs_kesehatan || "",
        status_bpjs_kesehatan: form?.status_bpjs_kesehatan || "",
        nomor_bpjs_ketenagakerjaan: form?.nomor_bpjs_ketenagakerjaan || "",
        status_bpjs_ketenagakerjaan: form?.status_bpjs_ketenagakerjaan || "",
        asuransi_lainnya: form?.asuransi_lainnya || "",
        nomor_asuransi_lainnya: form?.nomor_asuransi_lainnya || "",
        file_kartu_keluarga: form?.file_kartu_keluarga || "",
        file_ktp: form?.file_ktp || "",
        file_akta_lahir: form?.file_akta_lahir || "",
        file_rekening_bank: form?.file_rekening_bank || "",
        id_cabor: form?.id_cabor || defaultValue?.id_cabor || "",
        id_induk_organisasi: form?.id_induk_organisasi || defaultValue?.id_induk_organisasi || "",
        id_organisasi: form?.id_organisasi || defaultValue?.id_organisasi || "",
        nomor_induk: id ? form?.nomor_induk || defaultValue?.nomor_induk || "" : "",
        tanggal_mulai: form?.tanggal_mulai || defaultValue?.tanggal_mulai || "",
        catatan: form?.catatan || defaultValue?.catatan || "",
      },
    });
  };

  const showProfilePDF = async (idDetail) => {
    const result = await axiosFetchProfilePDF({
      method: "GET",
      url: `${url}/${idDetail}`,
      auth: true,
    });
    if (result?.data) {
      const photoDataURL = await loadPhotoDataURL(result.data?.photo_url, user?.token);
      setProfilePDFData({
        ...result.data,
        photo_data_url: photoDataURL,
      });
      toggleProfilePDFViewer();
    }
  };

  return (
    <>
    <DataList
      title={title}
      coltype={coltype}
      pdfconfig={{ orientation: "landscape" }}
      pdflist={{
        columns: [
          "no",
          "nomor_induk",
          "nama_lengkap",
          "jenis_kelamin_code",
          "usia",
          "cabor_name",
          "klub_olahraga_name",
          "aktif",
        ],
      }}
      xlslist={{
        columns: [...visibleListColumns, ...sdmExtraDataColumns],
      }}
      dlist={{
        url,
        columns: [...visibleListColumns, "action"],
        filter: [
          { dtype: "q", name: "q", colsize: 3 },
          showKoniKabKotaFilter
            ? {
                dtype: "dropdown",
                name: "id_koni_kabkota",
                label: "KONI Kab/Kota",
                colsize: 3,
                options: koniKabKotaOptions,
                valueId: "id_organisasi",
                labelId: "__label",
                placeholder: "-Semua KONI Kab/Kota-",
              }
            : null,
          {
            dtype: "dropdown",
            name: "id_cabor",
            label: "Cabor",
            colsize: 3,
            options: caborOptions,
            valueId: "id_cabor",
            labelId: "__label",
            placeholder: "-Semua Cabor-",
          },
          groupName !== "ADMIN_KLUB"
            ? {
                dtype: "dropdown",
                name: "id_organisasi",
                label: "Klub Olahraga",
                colsize: 3,
                options: klubOptions,
                valueId: "id_organisasi",
                labelId: "__label",
                placeholder: "-Semua Klub Olahraga-",
              }
            : null,
          {
            dtype: "dropdown",
            name: "aktif",
            label: "Status",
            colsize: 3,
            options: statusAktifOptions,
            valueId: "id",
            labelId: "nama",
            placeholder: "-Semua Status-",
          },
        ].filter(Boolean),
        sort_by: ["nama_lengkap", "cabor_name", "klub_olahraga_name", "nomor_induk"],
      }}
      toolbar={[
        {
          label: "Download Form",
          icon: "fa-download",
          onClick: toggleEmptyFormPDFViewer,
        },
      ]}
      addconfig={
        canManage
          ? {
              columns: baseColumnsForm,
              title: `Tambah ${title}`,
              label_tool: `Tambah ${title}`,
              defaultValue: {},
              coltype: addColtype,
              size: "xl",
              saveDisabled: !isAddReadyToSave,
              saveHandle,
              onChangeForm: onAddFormChange,
              onCancel: resetAddLookup,
              onSuccess: resetAddLookup,
              children: (
                <div className="col-12">
                  <div
                    className={`alert mb-0 ${
                      addLookup.status === "registered"
                        ? "alert-warning"
                        : addLookup.status === "new" || addLookup.status === "available"
                          ? "alert-info"
                          : "alert-secondary"
                    }`}
                  >
                    {addLookup.message}
                  </div>
                </div>
              ),
            }
          : null
      }
      editconfig={
        canManage
          ? {
              columns: ["id_keanggotaan", "id_personal", ...editColumnsForm],
              title: `Ubah ${title}`,
              url,
              urlitem: url,
              cellid: "id_keanggotaan",
              coltype: editColtype,
              size: "xl",
              saveHandle,
              onChangeForm: onEditFormChange,
              normalizeItem: (item) => ({
                ...item,
                id_induk_organisasi: item?.id_induk_organisasi || "",
              }),
            }
          : null
      }
      detailconfig={{
        url,
        cellid: "id_keanggotaan",
        columns: detailColumns,
        title: `Detail ${title}`,
        size: "xl",
        tooltip: "Detail",
        icon: "fa-search",
        renderContent: (item) => (
          <SDMProfileDetail
            item={item}
            title={title}
            printId={sdmProfilePrintId(item?.id_keanggotaan)}
            educationUrl={`/sdm/${rolePath}/${item?.id_keanggotaan}/pendidikan`}
            achievementUrl={`/sdm/${rolePath}/${item?.id_keanggotaan}/prestasi`}
            certificationUrl={`/sdm/${rolePath}/${item?.id_keanggotaan}/sertifikasi`}
            achievementOptionsBaseUrl={`/sdm/${rolePath}/${item?.id_keanggotaan}/capaian`}
          />
        ),
        footerContent: ({ idDetail }) => (
          <button
            type="button"
            className="btn btn-danger"
            disabled={loadingProfilePDF}
            onClick={() => showProfilePDF(idDetail)}
          >
            <i className="fas fa-file-pdf mr-1" />
            {loadingProfilePDF ? "Memuat PDF..." : "PDF"}
          </button>
        ),
      }}
      delconfig={
				canDelete
					? {
							url,
              cellid: "id_keanggotaan",
              cellname: "nama_lengkap",
            }
          : null
      }
      activateconfig={
        canManage
          ? {
              url,
              cellid: "id_keanggotaan",
              cellname: "nama_lengkap",
              cellactivate: "aktif",
              labelactivate: "Aktifkan",
              labelinactivate: "Non-aktifkan",
              cfmactivate: "mengaktifkan",
              cfminactivate: "menonaktifkan",
              infoactivate: "diaktifkan",
              infoinactivate: "dinonaktifkan",
            }
          : null
      }
    />
    {profilePDFData &&
      getProfilePDFViewer({
        title: `Profil ${title}`,
        orientation: "portrait",
        modalSize: "xl",
        modalZIndex: 2000,
        pageStyle: {
          paddingTop: 36,
          paddingHorizontal: 36,
          paddingBottom: 24,
        },
        content: <SDMProfilePDF item={profilePDFData} title={title} />,
      })}
    {getEmptyFormPDFViewer({
      title: `Form ${title}`,
      orientation: "portrait",
      modalSize: "xl",
      pageStyle: {
        paddingTop: 36,
        paddingHorizontal: 36,
        paddingBottom: 24,
      },
      content: <SDMEmptyFormPDF title={title} />,
    })}
    </>
  );
}
